/**
 * Supabase RPC 调用封装
 * 提供类型安全的 RPC 函数调用
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import type { RpcFunctions } from "./types";

// ==================== 错误类型 ====================

export class RpcError extends Error {
  constructor(
    public readonly functionName: string,
    public readonly detail: string,
  ) {
    super(`RPC ${functionName} 失败: ${detail}`);
    this.name = "RpcError";
  }
}

// ==================== 通用 RPC 调用器 ====================

async function callRpc<K extends keyof RpcFunctions>(
  supabase: SupabaseClient,
  fn: K,
  params: RpcFunctions[K]["params"],
): Promise<RpcFunctions[K]["result"]> {
  const { data, error } = await supabase.rpc(fn as string, params as Record<string, unknown>);

  if (error) {
    throw new RpcError(fn as string, error.message);
  }

  return data as RpcFunctions[K]["result"];
}

// ==================== 积分/托管 RPC ====================

/**
 * 发布悬赏时冻结 BBP
 */
export async function freezeBbpForBounty(
  supabase: SupabaseClient,
  bountyId: string,
  userId: string,
  amount: number,
): Promise<void> {
  if (amount <= 0) {
    throw new RpcError("freeze_bbp_for_bounty", "冻结金额必须大于 0");
  }
  await callRpc(supabase, "freeze_bbp_for_bounty", {
    p_bounty_id: bountyId,
    p_user_id: userId,
    p_amount: amount,
  });
}

/**
 * 完成悬赏时结算
 */
export async function settleBounty(
  supabase: SupabaseClient,
  bountyId: string,
): Promise<void> {
  await callRpc(supabase, "settle_bounty", {
    p_bounty_id: bountyId,
  });
}

/**
 * 取消/退款悬赏
 */
export async function refundBounty(
  supabase: SupabaseClient,
  bountyId: string,
): Promise<void> {
  await callRpc(supabase, "refund_bounty", {
    p_bounty_id: bountyId,
  });
}

// ==================== 充值 RPC ====================

/**
 * 后台确认充值入账
 */
export async function confirmDeposit(
  supabase: SupabaseClient,
  depositId: string,
  reviewerId: string,
  reviewNote?: string,
): Promise<void> {
  await callRpc(supabase, "confirm_deposit", {
    p_deposit_id: depositId,
    p_reviewer_id: reviewerId,
    p_review_note: reviewNote ?? null,
  });
}

// ==================== 邀请码 RPC ====================

/**
 * 验证并消耗邀请码
 */
export async function useInvitationCode(
  supabase: SupabaseClient,
  code: string,
  userId: string,
): Promise<void> {
  await callRpc(supabase, "use_invitation_code", {
    p_code: code,
    p_user_id: userId,
  });
}

// ==================== 交付管理 RPC ====================

/**
 * 提交交付（含修改次数控制 + 自动验收倒计时）
 * @returns delivery_id
 */
export async function submitDeliveryV2(
  supabase: SupabaseClient,
  bountyId: string,
  content: string,
  links?: string[],
): Promise<string> {
  return callRpc(supabase, "submit_delivery_v2", {
    p_bounty_id: bountyId,
    p_content: content,
    p_links: links ?? null,
  });
}

/**
 * 要求工程师修改交付（企业方调用）
 * 状态回退: delivered → in_progress
 */
export async function requestDeliveryModification(
  supabase: SupabaseClient,
  deliveryId: string,
  bountyId: string,
  reviewNote?: string,
): Promise<boolean> {
  return callRpc(supabase, "request_delivery_modification", {
    p_delivery_id: deliveryId,
    p_bounty_id: bountyId,
    p_review_note: reviewNote ?? null,
  });
}

/**
 * 工程师放弃任务（退款 + 信誉惩罚）
 * 状态回退: in_progress → open
 */
export async function abandonBounty(
  supabase: SupabaseClient,
  bountyId: string,
): Promise<boolean> {
  return callRpc(supabase, "abandon_bounty", {
    p_bounty_id: bountyId,
  });
}

// ==================== 竞标管理 RPC ====================

/**
 * 工程师提交竞标方案
 * 调用 submit_bid() RPC（数据库端执行校验+状态变更）
 */
export async function submitBid(
  supabase: SupabaseClient,
  bountyId: string,
  engineerId: string,
  proposal: string,
  approach: string,
  estimatedDays: number,
  proposedReward: number,
  relevantExperience: string,
  milestonePlan?: string,
  portfolioLinks?: string[],
  coverLetter?: string,
): Promise<{ success: boolean; bidId?: string; error?: string }> {
  return callRpc(supabase, "submit_bid", {
    p_bounty_id: bountyId,
    p_engineer_id: engineerId,
    p_proposal: proposal,
    p_approach: approach,
    p_estimated_days: estimatedDays,
    p_proposed_reward: proposedReward,
    p_relevant_experience: relevantExperience,
    p_milestone_plan: milestonePlan ?? null,
    p_portfolio_links: portfolioLinks ?? null,
    p_cover_letter: coverLetter ?? null,
  });
}

/**
 * 发布方选择中标工程师
 * 调用 select_bidder() RPC（数据库端原子操作：通过 bid_id 查找 engineer_id + 更新中标方案+拒绝其他+更新bounty状态+设置claimed_by）
 * RPC 签名: select_bidder(p_bounty_id, p_publisher_id, p_bid_id)
 */
export async function selectBidder(
  supabase: SupabaseClient,
  bountyId: string,
  bidId: string,
  publisherId: string,
): Promise<{ success: boolean; selectedEngineerId?: string; warning?: string | null; error?: string }> {
  return callRpc(supabase, "select_bidder", {
    p_bounty_id: bountyId,
    p_publisher_id: publisherId,
    p_bid_id: bidId,
  });
}

// ==================== 交付审查 RPC ====================

/**
 * 提交审查轮次交付物
 * 调用 submit_delivery_review() RPC
 */
export async function submitDeliveryReview(
  supabase: SupabaseClient,
  bountyId: string,
  engineerId: string,
  submission: { repo_url: string; description_files?: Array<{ name: string; url: string }>; message: string },
): Promise<{ success: boolean; deliveryReviewId?: string; error?: string }> {
  return callRpc(supabase, "submit_delivery_review", {
    p_bounty_id: bountyId,
    p_engineer_id: engineerId,
    p_submission: submission,
  });
}

/**
 * 审核交付物（通过/驳回）
 * 调用 review_delivery() RPC
 */
export async function reviewDelivery(
  supabase: SupabaseClient,
  deliveryReviewId: string,
  publisherId: string,
  status: "approved" | "rejected",
  feedback?: string | null,
): Promise<{ success: boolean; bountyStatus?: string; error?: string }> {
  return callRpc(supabase, "review_delivery", {
    p_delivery_review_id: deliveryReviewId,
    p_publisher_id: publisherId,
    p_status: status,
    p_feedback: feedback ?? null,
  });
}
